// We will add getEnrollmentsByInstructorId to FirebaseContextType declarations
// and to the returned context value, making sure it's properly typed and accessible.

import React, { createContext, useContext, useEffect, useState } from "react";
import { 
  User as FirebaseUser,
  signInWithEmailAndPassword as signInWithEmail,
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile
} from "firebase/auth";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  getDoc,
  setDoc,
  query,
  where,
  orderBy,
  DocumentData,
  serverTimestamp,
  or,
  QueryCompositeFilterConstraint,
  onSnapshot,
  updateDoc,
  deleteDoc
} from "firebase/firestore";
import { app, auth, db, isDemoMode } from "../config/firebase";
import { subscribeToMessages as subscribeToMessagesUtil } from "../utils/firebase";
import { 
  getEnrollmentsByUserId as getEnrollmentsByUserIdUtil,
  getEnrollmentsByInstructorId as getEnrollmentsByInstructorIdUtil
} from "../utils/firestoreService";
import { Enrollment } from "../utils/firestoreService";

interface FirebaseContextType {
  currentUser: FirebaseUser | null;
  user: DocumentData | null;
  loading: boolean;
  registerWithEmail: (email: string, password: string, firstName: string, lastName: string, role: string, phone?: string, address?: string, postcode?: string, gearbox?: string) => Promise<FirebaseUser | null>;
  loginWithEmail: (email: string, password: string) => Promise<FirebaseUser | null>;
  logout: () => Promise<void>;
  createUser: (userData: any) => Promise<string>;
  getUserByEmail: (email: string) => Promise<DocumentData | null>;
  getAllUsers: () => Promise<DocumentData[]>;
  getAllInstructors: () => Promise<DocumentData[]>;
  getMessages: (senderId: string, recipientId: string) => Promise<any[]>;
  sendMessage: (messageData: any) => Promise<string>;
  checkConversationPermission: (userId1: string, userId2: string) => Promise<{permitted: boolean, reason?: string}>;
  isDemoMode: boolean;
  subscribeToMessages: (senderId: string, recipientId: string, callback: (messages: any[]) => void) => () => void;
  updateUser: (userId: string, userData: Partial<DocumentData>) => Promise<void>;
  getFirestore: () => any;
  getEnrollmentsByUserId: (userId: string) => Promise<Enrollment[]>;
  getEnrollmentsByInstructorId: (instructorId: string) => Promise<Enrollment[]>;
  deleteUser: (userId: string) => Promise<void>;
}

const FirebaseContext = createContext<FirebaseContextType | undefined>(undefined);

export const FirebaseProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [user, setUser] = useState<DocumentData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user && db) {
        try {
          const userDoc = await getUserByFirebaseId(user.uid);
          
          if (!userDoc) {
            console.log("User exists in Auth but not in Firestore, creating Firestore record");
            
            await setDoc(doc(db, "users", user.uid), {
              id: user.uid,
              email: user.email,
              displayName: user.displayName || "User",
              createdAt: new Date().toISOString(),
              status: "active"
            });
          } else {
            setUser(userDoc);
          }
        } catch (error) {
          console.error("Error synchronizing user data:", error);
        }
      } else {
        setUser(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Initialize admin user
  useEffect(() => {
    const initializeAdminUser = async () => {
      if (isDemoMode) return;
      
      try {
        // First check if admin exists in Firestore
        const usersRef = collection(db, "users");
        const adminQuery = query(usersRef, where("email", "==", "admin@gmail.com"));
        const adminDocs = await getDocs(adminQuery);
        
        if (!adminDocs.empty) {
          console.log("Admin user already exists in Firestore");
          return;
        }

        console.log("No admin found in Firestore, attempting to create...");
        
        try {
          // Try to create admin in Firebase Auth
          const adminCredential = await createUserWithEmailAndPassword(
            auth,
            "admin@gmail.com",
            "admin123"
          );

          if (adminCredential.user) {
            // Update admin profile
            await updateProfile(adminCredential.user, {
              displayName: "Administrator"
            });

            // Create admin in Firestore
            const adminData = {
              id: adminCredential.user.uid,
              email: "admin@gmail.com",
              firstName: "Administrator",
              lastName: "Admin",
              displayName: "Administrator",
              role: "admin",
              status: "active",
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
              phone: "",
              address: "",
              postcode: "",
            };

            await setDoc(doc(db, "users", adminCredential.user.uid), adminData);
            console.log("Admin user created successfully");
          }
        } catch (createError: any) {
          if (createError.code === 'auth/email-already-in-use') {
            console.log("Admin exists in Auth but not in Firestore, creating Firestore record...");
            try {
              // Try to sign in as admin
              const adminAuth = await signInWithEmail(auth, "admin@gmail.com", "admin123");
              if (adminAuth.user) {
                const adminData = {
                  id: adminAuth.user.uid,
                  email: "admin@gmail.com",
            firstName: "Administrator",
                  lastName: "Admin",
                  displayName: "Administrator",
            role: "admin",
                  status: "active",
                  createdAt: serverTimestamp(),
                  updatedAt: serverTimestamp(),
                  phone: "",
                  address: "",
                  postcode: "",
                };
                await setDoc(doc(db, "users", adminAuth.user.uid), adminData);
                console.log("Admin Firestore record created successfully");
              }
            } catch (signInError) {
              console.error("Could not sign in as admin:", signInError);
            }
          } else {
            console.error("Error creating admin user:", createError);
          }
        }
      } catch (error) {
        console.error("Error in admin user initialization:", error);
      }
    };

      initializeAdminUser();
  }, []);

  const getUserByFirebaseId = async (firebaseId: string): Promise<DocumentData | null> => {
    if (!db) return null;
    
    try {
      const userRef = doc(db, "users", firebaseId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        return { id: userSnap.id, ...userSnap.data() };
      }
      
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("id", "==", firebaseId));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        return { id: querySnapshot.docs[0].id, ...querySnapshot.docs[0].data() };
      }
      
      return null;
    } catch (error) {
      console.error("Error getting user by Firebase ID:", error);
      return null;
    }
  };

  const registerWithEmail = async (
    email: string, 
    password: string, 
    firstName: string, 
    lastName: string, 
    role: string = "student", 
    phone?: string, 
    address?: string, 
    postcode?: string, 
    gearbox?: string,
    area?: string,
    instructorId?: string
  ) => {
    if (!auth || !db) return null;
    
    try {
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Update profile display name
      await updateProfile(user, {
          displayName: `${firstName} ${lastName}`
        });
        
      // Create user document in Firestore
        const userData = {
        id: user.uid,
          email: email,
          firstName: firstName,
          lastName: lastName,
          displayName: `${firstName} ${lastName}`,
          role: role,
          hasAccount: true, // Account created via registration
          status: "active",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
          phone: phone || "",
          address: address || "",
          postcode: postcode || "",
        gearbox: gearbox || "manual",
        area: area || "",
        instructorId: instructorId || ""
      };
      
      await setDoc(doc(db, "users", user.uid), userData);
      
      // Update local state
      setUser(userData);
      setCurrentUser(user);
      
      return user;
    } catch (error) {
      console.error("Error in registration:", error);
      throw error;
    }
  };

  const loginWithEmail = async (email: string, password: string) => {
    if (isDemoMode) {
      console.warn("Firebase is in demo mode - login simulated");
      return null;
    }
    
    if (!auth) return null;
    
    try {
      const result = await signInWithEmail(auth, email, password);
      
      if (result.user && db) {
        try {
          const userDoc = await getUserByFirebaseId(result.user.uid);
          
          if (!userDoc) {
            console.log("User exists in Auth but not in Firestore, creating Firestore record");
            
            // Get user data from email
            const emailUser = await getUserByEmail(email);
            
            if (emailUser) {
              // If user exists by email, use that data
              const userData = {
                ...emailUser,
                id: result.user.uid,
                updatedAt: new Date().toISOString()
              };
              await setDoc(doc(db, "users", result.user.uid), userData);
            } else {
              // Create new user document with basic data
              const userData = {
                id: result.user.uid,
                email: result.user.email,
                firstName: result.user.displayName?.split(' ')[0] || "",
                lastName: result.user.displayName?.split(' ')[1] || "",
                displayName: result.user.displayName || "User",
                role: "student", // Default role
                status: "active",
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                phone: "",
                address: "",
                postcode: "",
                gearbox: ""
              };
              await setDoc(doc(db, "users", result.user.uid), userData);
            }
          } else if (!userDoc.role) {
            // If user exists but has no role, update with default role
            console.log("Updating missing role in Firestore...");
            await updateDoc(doc(db, "users", result.user.uid), {
              role: "student",
              updatedAt: new Date().toISOString()
            });
            console.log("Role updated in Firestore");
          }
        } catch (error) {
          console.error("Error synchronizing user data on login:", error);
        }
      }
      
      return result.user;
    } catch (error) {
      console.error("Error in loginWithEmail:", error);
      throw error;
    }
  };

  const logout = async () => {
    if (isDemoMode) {
      console.warn("Firebase is in demo mode - logout simulated");
      return;
    }
    
    if (!auth) return;
    
    return signOut(auth);
  };

  const createUser = async (userData: any) => {
    if (!db) throw new Error("Firestore is not initialized");
    
    try {
      const existingUser = await getUserByEmail(userData.email);
      if (existingUser) {
        return existingUser.id;
      }
      
      // Standardize user data structure
      const standardizedUserData = {
        id: userData.id || doc(collection(db, "users")).id,
        email: userData.email,
        firstName: userData.firstName || "",
        lastName: userData.lastName || "",
        displayName: userData.displayName || `${userData.firstName || ""} ${userData.lastName || ""}`.trim() || "User",
        role: userData.role || "student",
        status: userData.status || "active",
        phone: userData.phone || "",
        address: userData.address || "",
        postcode: userData.postcode || "",
        gearbox: userData.gearbox || "",
        createdAt: userData.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      // Add role-specific fields
      if (standardizedUserData.role === "instructor") {
        Object.assign(standardizedUserData, {
          licenseNumber: userData.licenseNumber || "",
          experience: userData.experience || "",
          carModel: userData.carModel || "",
          specialties: userData.specialties || [],
          availability: userData.availability || "",
          students: 0
        });
      }
      
      const userRef = doc(db, "users", standardizedUserData.id);
      await setDoc(userRef, standardizedUserData);
      
      return standardizedUserData.id;
    } catch (error) {
      console.error("Error creating user in Firestore:", error);
      throw error;
    }
  };

  const getUserByEmail = async (email: string) => {
    if (!db) throw new Error("Firestore is not initialized");
    
    try {
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("email", "==", email));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      const userDoc = querySnapshot.docs[0];
      return { id: userDoc.id, ...userDoc.data() };
    } catch (error) {
      console.error("Error getting user by email:", error);
      throw error;
    }
  };

  const getAllUsers = async () => {
    if (!db) throw new Error("Firestore is not initialized");
    
    try {
      const usersRef = collection(db, "users");
      const querySnapshot = await getDocs(usersRef);
      
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error("Error getting all users:", error);
      throw error;
    }
  };

  const getAllInstructors = async () => {
    if (!db) throw new Error("Firestore is not initialized");
    
    try {
      // First get all instructors
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("role", "==", "instructor"));
      const querySnapshot = await getDocs(q);
      
      // Get all enrollments
      const enrollmentsRef = collection(db, "enrollments");
      const enrollmentsSnapshot = await getDocs(enrollmentsRef);
      const enrollments = enrollmentsSnapshot.docs.map(doc => doc.data());
      
      // Count students for each instructor
      const instructors = querySnapshot.docs.map(doc => {
        const instructorData = doc.data();
        const instructorId = doc.id;
        
        // Count unique students for this instructor
        const uniqueStudentIds = new Set(
          enrollments
            .filter(enrollment => enrollment.instructorId === instructorId)
            .map(enrollment => enrollment.userId)
        );
        
        return {
          id: instructorId,
          ...instructorData,
          students: uniqueStudentIds.size // Update the students count
        };
      });
      
      return instructors;
    } catch (error) {
      console.error("Error getting instructors:", error);
      throw error;
    }
  };

  const checkConversationPermission = async (userId1: string, userId2: string) => {
    if (isDemoMode) {
      console.log("Firebase is in demo mode - conversation permission check simulated");
      return { permitted: true };
    }
    
    if (!db) throw new Error("Firestore is not initialized");
    
    try {
      console.log(`Checking conversation permission between ${userId1} and ${userId2}`);
      
      // Always allow admin messaging
      const usersRef = collection(db, "users");
      
      const user1Query = query(usersRef, where("id", "==", userId1));
      const user1Snapshot = await getDocs(user1Query);
      
      if (!user1Snapshot.empty && user1Snapshot.docs[0].data().role === 'admin') {
        console.log("Permission granted: user1 is an admin");
        return { permitted: true };
      }
      
      const user2Query = query(usersRef, where("id", "==", userId2));
      const user2Snapshot = await getDocs(user2Query);
      
      if (!user2Snapshot.empty && user2Snapshot.docs[0].data().role === 'admin') {
        console.log("Permission granted: user2 is an admin");
        return { permitted: true };
      }
      
      // Just track the conversation for record-keeping
      try {
        const conversationsRef = collection(db, "conversations");
        const connectionId = [userId1, userId2].sort().join("_");
        
        await setDoc(doc(db, "conversations", connectionId), {
          users: [userId1, userId2],
          updatedAt: new Date().toISOString()
        }, { merge: true });
        
        console.log(`Saved conversation connection between ${userId1} and ${userId2}`);
      } catch (error) {
        console.warn("Could not save conversation record:", error);
      }
      
      // In this simplified version, we allow all conversations by default
      console.log("Permission granted: direct messaging enabled");
      return { permitted: true };
    } catch (error) {
      console.error("Error checking conversation permission:", error);
      return { 
        permitted: false, 
        reason: `Something went wrong. Please try again.` 
      };
    }
  };

  const getMessages = async (senderId: string, recipientId: string) => {
    if (isDemoMode || !db) {
      console.warn("Firebase is in demo mode - using mock messages");
      return [
        {
          id: "1",
          senderId: senderId,
          senderName: "Instructor",
          recipientId: recipientId,
          content: "Hello, how are your driving lessons going?",
          timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          read: true
        },
        {
          id: "2",
          senderId: recipientId,
          senderName: "Student",
          recipientId: senderId,
          content: "Great! I've been practicing parallel parking.",
          timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
          read: true
        },
        {
          id: "3",
          senderId: senderId,
          senderName: "Instructor",
          recipientId: recipientId,
          content: "That's excellent! We'll focus on that in our next lesson.",
          timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
          read: true
        }
      ];
    }
    
    try {
      const permissionResult = await checkConversationPermission(senderId, recipientId);
      
      if (!permissionResult.permitted) {
        console.warn(`No permission to view messages: ${permissionResult.reason}`);
        return [];
      }
      
      console.log(`Getting messages between ${senderId} and ${recipientId}`);
      const messagesRef = collection(db, "messages");
      
      const participant1 = senderId + "_" + recipientId;
      const participant2 = recipientId + "_" + senderId;
      
      try {
        const q = query(
          messagesRef,
          where("participants", "array-contains-any", [participant1, participant2]),
          orderBy("timestamp", "asc")
        );
        
        const querySnapshot = await getDocs(q);
        
        const messages: any[] = [];
        querySnapshot.forEach((doc) => {
          messages.push({ id: doc.id, ...doc.data() });
        });
        
        console.log(`Found ${messages.length} messages`);
        return messages.sort((a, b) => {
          return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
        });
      } catch (error: any) {
        if (error.code === "failed-precondition" && error.message.includes("index")) {
          console.warn("Index error, falling back to unordered query:", error.message);
          
          const fallbackQuery = query(
            messagesRef,
            where("participants", "array-contains-any", [participant1, participant2])
          );
          
          const fallbackSnapshot = await getDocs(fallbackQuery);
          
          const messages: any[] = [];
          fallbackSnapshot.forEach((doc) => {
            messages.push({ id: doc.id, ...doc.data() });
          });
          
          return messages.sort((a, b) => {
            return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
          });
        } else {
          throw error;
        }
      }
    } catch (error) {
      console.error("Error getting messages:", error);
      return [];
    }
  };

  const sendMessage = async (messageData: {
    senderId: string;
    senderName: string;
    recipientId: string;
    content: string;
    timestamp: string;
    read: boolean;
  }) => {
    if (isDemoMode) {
      console.warn("Firebase is in demo mode - message sending simulated");
      return "mock-message-id-" + Date.now();
    }
    
    if (!db) throw new Error("Firestore is not initialized");
    
    try {
      const permissionResult = await checkConversationPermission(
        messageData.senderId, 
        messageData.recipientId
      );
      
      if (!permissionResult.permitted) {
        throw new Error(`Cannot send message: ${permissionResult.reason}`);
      }
      
      console.log("Sending message:", messageData);
      
      const participant1 = messageData.senderId + "_" + messageData.recipientId;
      const participant2 = messageData.recipientId + "_" + messageData.senderId;
      
      const messageWithParticipants = {
        ...messageData,
        participants: [participant1, participant2],
        timestamp: messageData.timestamp || new Date().toISOString()
      };
      
      const messagesRef = collection(db, "messages");
      const docRef = await addDoc(messagesRef, messageWithParticipants);
      
      console.log(`Message sent with ID: ${docRef.id}`);
      
      await updateOrCreateConversation(messageData.senderId, messageData.recipientId, messageData.content);
      
      return docRef.id;
    } catch (error) {
      console.error("Error sending message:", error);
      throw error;
    }
  };

  const updateOrCreateConversation = async (user1Id: string, user2Id: string, lastMessage: string) => {
    if (isDemoMode || !db) return;
    
    try {
      const users = [user1Id, user2Id].sort();
      const conversationId = users.join("_");
      
      const conversationRef = doc(db, "conversations", conversationId);
      
      await setDoc(conversationRef, {
        participants: [user1Id, user2Id],
        lastMessage,
        updatedAt: new Date().toISOString(),
      }, { merge: true });
      
      console.log(`Conversation ${conversationId} updated`);
    } catch (error) {
      console.error("Error updating conversation:", error);
    }
  };

  const subscribeToMessages = (
    senderId: string, 
    recipientId: string, 
    callback: (messages: any[]) => void
  ) => {
    if (isDemoMode) {
      // For demo mode, simulate real-time updates with initial demo data
      console.warn("Firebase is in demo mode - using mock messages subscription");
      setTimeout(() => {
        callback([
          {
            id: "1",
            senderId: senderId,
            senderName: "Instructor",
            recipientId: recipientId,
            content: "Hello, how are your driving lessons going?",
            timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
            read: true
          },
          {
            id: "2",
            senderId: recipientId,
            senderName: "Student",
            recipientId: senderId,
            content: "Great! I've been practicing parallel parking.",
            timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
            read: true
          },
          {
            id: "3",
            senderId: senderId,
            senderName: "Instructor",
            recipientId: recipientId,
            content: "That's excellent! We'll focus on that in our next lesson.",
            timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
            read: true
          }
        ]);
      }, 500);
      return () => {};
    }
    
    return subscribeToMessagesUtil(senderId, recipientId, callback);
  };

  const getFirestore = () => {
    return db;
  };

  const updateUser = async (userId: string, userData: Partial<DocumentData>) => {
    if (!db) throw new Error("Firestore is not initialized");
    
    try {
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, {
        ...userData,
        updatedAt: new Date().toISOString()
      });
      
      console.log(`User ${userId} updated with data:`, userData);
    } catch (error) {
      console.error("Error updating user:", error);
      throw error;
    }
  };

  const getEnrollmentsByUserId = async (userId: string): Promise<Enrollment[]> => {
    return getEnrollmentsByUserIdUtil(userId);
  };

  const getEnrollmentsByInstructorId = async (instructorId: string): Promise<Enrollment[]> => {
    return getEnrollmentsByInstructorIdUtil(instructorId);
  };

  const deleteUser = async (userId: string) => {
    try {
      // Delete user document from Firestore
      await deleteDoc(doc(db, "users", userId));
    } catch (error) {
      console.error("Error deleting user:", error);
      throw error;
    }
  };

  const value = {
    currentUser,
    user,
    loading,
    registerWithEmail,
    loginWithEmail,
    logout,
    createUser,
    getUserByEmail,
    getAllUsers,
    getAllInstructors,
    getMessages,
    sendMessage,
    checkConversationPermission,
    isDemoMode,
    subscribeToMessages,
    updateUser,
    getFirestore,
    getEnrollmentsByUserId,
    getEnrollmentsByInstructorId,
    deleteUser,
  };

  return (
    <FirebaseContext.Provider value={value}>
      {!loading && children}
    </FirebaseContext.Provider>
  );
};

export const useFirebase = () => {
  const context = useContext(FirebaseContext);
  if (context === undefined) {
    throw new Error('useFirebase must be used within a FirebaseProvider');
  }
  return context;
};

export default FirebaseProvider;

